import { useState } from 'react';

import {
  OffCycleRule,
  type CycleStandingResponse,
  type EligibleMovementResponse,
  type NoticeResponse,
  type SubmissionChannel,
} from '../../api';
import {
  AsyncSection,
  DataTable,
  Form,
  FormError,
  Panel,
  SelectField,
  SubmitButton,
  TextField,
  useNotifications,
  type Column,
} from '../../components';
import { formatDate, formatDateTime } from '../../format';
import { saveAsAttachment } from '../../utils/download';
import {
  useEligibleMovements,
  useMarkNoticeFailed,
  useMarkNoticeSent,
  useNoticeFile,
  useOffCycleNotices,
  useRaiseOffCycleNotice,
} from '../queries';
import {
  ACTION_LABELS,
  NOTICE_STATUS_LABELS,
  OFF_CYCLE_COPY,
  RULE_LABELS,
  isOpenNotice,
  proofOfNotice,
  selectableWith,
  waitLabel,
} from './offCycle';
import { CHANNEL_LABELS, CHANNEL_OPTIONS } from './submissionCycle';

/**
 * The exception route, beside the month rather than inside it.
 *
 * The monthly panel above answers "what still has to go out this month?". This
 * one answers a different question that the same analyst asks on the same
 * screen: **is anybody going to be uninsured while they wait for it?** A person
 * admitted four days after the cutoff lands on a file three weeks away, and
 * until now nothing said so out loud.
 *
 * Two things about the design are worth stating because they are decisions.
 *
 * **The waiting list is shown whatever the contract says.** On a contract whose
 * insurer processes only the monthly file, nothing here can be notified -- and
 * the honest answer is still that these people are waiting, so the list is there
 * and the panel says in as many words why there is no button. Hiding it would
 * leave an analyst believing the question had no answer.
 *
 * **Selecting one movement narrows the rest to its action.** One notice carries
 * one kind of movement, because the insurer's layout says who somebody is and
 * not what is happening to them. The server refuses a mixture; disabling the
 * others is how the rule gets explained before somebody hits it.
 */
export function OffCyclePanel({ standing }: { standing: CycleStandingResponse }) {
  const eligible = useEligibleMovements(standing.contract_id);
  const notices = useOffCycleNotices(standing.contract_id);
  const [selected, setSelected] = useState<readonly EligibleMovementResponse[]>([]);

  return (
    <Panel title={OFF_CYCLE_COPY.title} description={OFF_CYCLE_COPY.description}>
      <AsyncSection
        state={eligible}
        loadingLabel="Carregando as movimentações em espera…"
        errorTitle="Não foi possível carregar as movimentações em espera"
      >
        {(data) => (
          <>
            <dl className="definitions">
              <div>
                <dt>{OFF_CYCLE_COPY.ruleLabel}</dt>
                <dd>{RULE_LABELS[data.off_cycle_rule as OffCycleRule]}</dd>
              </div>
              <div>
                <dt>{OFF_CYCLE_COPY.waitingTitle}</dt>
                <dd>{data.count}</dd>
              </div>
            </dl>

            {data.off_cycle_rule === OffCycleRule.NOT_PERMITTED && (
              <p className="panel__footnote panel__footnote--warning">
                {OFF_CYCLE_COPY.notPermitted}
              </p>
            )}
            {data.off_cycle_rule === OffCycleRule.ADMISSIONS_ONLY && (
              <p className="panel__footnote">{OFF_CYCLE_COPY.admissionsOnly}</p>
            )}

            {data.items.length === 0 ? (
              <p className="panel__footnote">{OFF_CYCLE_COPY.noWaiting}</p>
            ) : (
              <>
                <DataTable
                  id="off-cycle-waiting"
                  caption={OFF_CYCLE_COPY.waitingDescription}
                  columns={waitingColumns(selected, setSelected)}
                  rows={[...data.items]}
                  rowKey={(row) => row.delta.id}
                  emptyTitle="Nada aguardando"
                />
                {selected.length > 0 && (
                  <RaiseNoticeForm
                    contractId={standing.contract_id}
                    companyId={standing.company_id}
                    selected={selected}
                    onDone={() => setSelected([])}
                    onCancel={() => setSelected([])}
                  />
                )}
              </>
            )}
          </>
        )}
      </AsyncSection>

      <AsyncSection
        state={notices}
        loadingLabel="Carregando os avisos…"
        errorTitle="Não foi possível carregar os avisos"
        isEmpty={(data) => data.items.length === 0}
        emptyTitle="Nenhum aviso"
        emptyDescription={OFF_CYCLE_COPY.noNotices}
      >
        {(data) => (
          <>
            <h3 className="panel__subheading">{OFF_CYCLE_COPY.noticesTitle}</h3>
            <p className="panel__footnote">{OFF_CYCLE_COPY.noticesDescription}</p>
            {data.items.map((notice) => (
              <NoticeRow key={notice.id} notice={notice} />
            ))}
          </>
        )}
      </AsyncSection>

      <p className="panel__footnote">{OFF_CYCLE_COPY.stillOnTheFile}</p>
      <p className="panel__footnote">{OFF_CYCLE_COPY.noTransmission}</p>
    </Panel>
  );
}

/**
 * One waiting movement per line, with how long the wait is and whether it can go.
 *
 * The countdown is the column this table exists for. Everything else is context
 * for deciding whether that number is a problem, which is a judgement the screen
 * does not make on the analyst's behalf.
 */
function waitingColumns(
  selected: readonly EligibleMovementResponse[],
  setSelected: (next: readonly EligibleMovementResponse[]) => void,
): readonly Column<EligibleMovementResponse>[] {
  const isSelected = (row: EligibleMovementResponse) =>
    selected.some((item) => item.delta.id === row.delta.id);
  return [
    {
      key: 'pick',
      header: 'Avisar',
      render: (row) => (
        <input
          type="checkbox"
          aria-label={`Incluir ${ACTION_LABELS[row.delta.action]} de ${formatDate(row.delta.effective_date)} no aviso`}
          checked={isSelected(row)}
          disabled={!selectableWith(row, selected)}
          onChange={() =>
            setSelected(
              isSelected(row)
                ? selected.filter((item) => item.delta.id !== row.delta.id)
                : [...selected, row],
            )
          }
        />
      ),
    },
    {
      key: 'action',
      header: 'Movimentação',
      render: (row) => ACTION_LABELS[row.delta.action],
    },
    {
      key: 'effective',
      header: 'Vigência',
      render: (row) => formatDate(row.delta.effective_date),
    },
    { key: 'cycle', header: 'Vai no ciclo', render: (row) => row.cycle_month },
    {
      key: 'wait',
      header: 'Espera',
      render: (row) => (
        <>
          {waitLabel(row)}
          <br />
          <span className="table__muted">Corte em {formatDate(row.cutoff_date)}</span>
        </>
      ),
    },
    {
      key: 'notifiable',
      header: 'Fora do ciclo',
      render: (row) =>
        row.is_notifiable ? (
          <span className="badge badge--ok">Pode ser avisada</span>
        ) : (
          <span className="table__muted">Espera o arquivo</span>
        ),
    },
  ];
}

/** The notice, and the reason it could not wait -- which is mandatory. */
function RaiseNoticeForm({
  contractId,
  companyId,
  selected,
  onDone,
  onCancel,
}: {
  contractId: string;
  companyId: string;
  selected: readonly EligibleMovementResponse[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const { notify } = useNotifications();
  const raise = useRaiseOffCycleNotice();
  const [reason, setReason] = useState('');

  return (
    <Form
      aria-label={OFF_CYCLE_COPY.raise}
      onSubmit={() =>
        raise.mutate(
          {
            contractId,
            companyId,
            deltaIds: selected.map((item) => item.delta.id),
            reason: reason.trim(),
          },
          {
            onSuccess: () => {
              notify('success', OFF_CYCLE_COPY.raised);
              onDone();
            },
          },
        )
      }
    >
      <FormError error={raise.error} />
      <p className="panel__footnote">{OFF_CYCLE_COPY.selectionHint}</p>
      <TextField
        label={OFF_CYCLE_COPY.reasonLabel}
        hint={OFF_CYCLE_COPY.reasonHint}
        required
        value={reason}
        onChange={setReason}
      />
      <div className="form__actions">
        <SubmitButton pending={raise.isPending}>{OFF_CYCLE_COPY.raise}</SubmitButton>
        <button type="button" className="button button--ghost" onClick={onCancel}>
          {OFF_CYCLE_COPY.cancel}
        </button>
      </div>
    </Form>
  );
}

/** One notice, its evidence, and the one thing there is to do about it next. */
function NoticeRow({ notice }: { notice: NoticeResponse }) {
  const [marking, setMarking] = useState<'sent' | 'failed' | null>(null);
  const file = useNoticeFile();
  const { notify } = useNotifications();
  const proof = proofOfNotice(notice);

  return (
    <div className="panel__section">
      <dl className="definitions">
        <div>
          <dt>Movimentação</dt>
          <dd>
            {ACTION_LABELS[notice.action]} · {notice.item_count}
          </dd>
        </div>
        <div>
          <dt>Situação</dt>
          <dd>{NOTICE_STATUS_LABELS[notice.status]}</dd>
        </div>
        <div>
          <dt>Gerado</dt>
          <dd>
            {notice.raised_by.display_name} em {formatDateTime(notice.raised_at)}
          </dd>
        </div>
        <div>
          <dt>Motivo</dt>
          <dd>{notice.reason}</dd>
        </div>
        <div>
          <dt>Também vai no ciclo</dt>
          <dd>{notice.cycle_month}</dd>
        </div>
        <div>
          <dt>Arquivo</dt>
          <dd>
            <code>{notice.file_name}</code>
          </dd>
        </div>
        {notice.sent_at != null && (
          <>
            <div>
              <dt>Comunicado</dt>
              <dd>
                {notice.sent_by?.display_name ?? '—'} em {formatDateTime(notice.sent_at)}
              </dd>
            </div>
            <div>
              <dt>Por onde</dt>
              <dd>{notice.channel != null ? CHANNEL_LABELS[notice.channel] : '—'}</dd>
            </div>
            <div>
              <dt>Comprovação</dt>
              {/* Never empty on a communicated notice -- the server refuses the
                  marker without one -- but rendered from the data rather than
                  asserted, so one that somehow had none would say so loudly. */}
              <dd>{proof.length > 0 ? proof.join(' · ') : 'Nada registrado'}</dd>
            </div>
          </>
        )}
        {notice.failure_reason != null && (
          <div>
            <dt>Recusa</dt>
            <dd>{notice.failure_reason}</dd>
          </div>
        )}
      </dl>

      {/* The file, beside the name the panel already shows. `GET
          /off-cycle-notices/{id}/file` is the one authenticated path to its
          bytes, and the name it downloads under is the one the server gave
          it -- deliberately not the monthly file's name, so the two cannot
          be confused in an operations mailbox. */}
      <div className="form__actions">
        <button
          type="button"
          className="button button--secondary"
          disabled={file.isPending}
          onClick={() =>
            file.mutate(
              { companyId: notice.company_id, noticeId: notice.id },
              {
                // The name the server sends the bytes under; the stored one
                // the panel already shows is the fallback, and the two agree.
                onSuccess: ({ blob, filename }) =>
                  saveAsAttachment(blob, filename ?? notice.file_name),
                onError: () => notify('error', OFF_CYCLE_COPY.downloadFailed),
              },
            )
          }
        >
          {file.isPending ? OFF_CYCLE_COPY.downloading : OFF_CYCLE_COPY.downloadFile}
        </button>
      </div>

      {isOpenNotice(notice) && marking === null && (
        <div className="form__actions">
          <button
            type="button"
            className="button button--primary"
            onClick={() => setMarking('sent')}
          >
            {OFF_CYCLE_COPY.markSent}
          </button>
          <button
            type="button"
            className="button button--secondary"
            onClick={() => setMarking('failed')}
          >
            {OFF_CYCLE_COPY.markFailed}
          </button>
        </div>
      )}
      {marking === 'sent' && <MarkNoticeSentForm notice={notice} onDone={() => setMarking(null)} />}
      {marking === 'failed' && (
        <MarkNoticeFailedForm notice={notice} onDone={() => setMarking(null)} />
      )}
    </div>
  );
}

/**
 * The marker, and the rule it will not let an analyst past.
 *
 * At least one of the two forms of proof offered here has to carry something.
 * The check is here *and* on the server, and that is not duplication with a
 * straight face: the server's is the rule, and this one exists so that somebody
 * who forgot the protocol number is told next to the field rather than after the
 * request goes.
 */
function MarkNoticeSentForm({ notice, onDone }: { notice: NoticeResponse; onDone: () => void }) {
  const { notify } = useNotifications();
  const markSent = useMarkNoticeSent();
  const [channel, setChannel] = useState<SubmissionChannel | ''>('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [showProofError, setShowProofError] = useState(false);

  const hasProof = [reference, notes].some((value) => value.trim() !== '');

  return (
    <Form
      aria-label={OFF_CYCLE_COPY.markSent}
      onSubmit={() => {
        if (!hasProof || channel === '') {
          setShowProofError(true);
          return;
        }
        markSent.mutate(
          {
            noticeId: notice.id,
            companyId: notice.company_id,
            channel,
            ...(reference.trim() !== '' ? { reference: reference.trim() } : {}),
            ...(notes.trim() !== '' ? { notes: notes.trim() } : {}),
          },
          {
            onSuccess: () => {
              notify('success', OFF_CYCLE_COPY.sent);
              onDone();
            },
          },
        );
      }}
    >
      <FormError error={markSent.error} />
      <SelectField
        label={OFF_CYCLE_COPY.channelLabel}
        hint={OFF_CYCLE_COPY.channelHint}
        required
        value={channel}
        onChange={setChannel}
        options={CHANNEL_OPTIONS}
        placeholder="Selecione"
      />
      <TextField
        label={OFF_CYCLE_COPY.referenceLabel}
        hint={OFF_CYCLE_COPY.referenceHint}
        value={reference}
        onChange={setReference}
      />
      <TextField
        label={OFF_CYCLE_COPY.notesLabel}
        hint={OFF_CYCLE_COPY.notesHint}
        value={notes}
        onChange={setNotes}
      />
      {showProofError && !hasProof && (
        <p className="field__error" role="alert">
          {OFF_CYCLE_COPY.proofRequired}
        </p>
      )}
      <div className="form__actions">
        <SubmitButton pending={markSent.isPending}>{OFF_CYCLE_COPY.confirmSent}</SubmitButton>
        <button type="button" className="button button--ghost" onClick={onDone}>
          {OFF_CYCLE_COPY.cancel}
        </button>
      </div>
    </Form>
  );
}

/** The refusal, with the reason that decides whether the contract's rule was right. */
function MarkNoticeFailedForm({ notice, onDone }: { notice: NoticeResponse; onDone: () => void }) {
  const { notify } = useNotifications();
  const markFailed = useMarkNoticeFailed();
  const [reason, setReason] = useState('');

  return (
    <Form
      aria-label={OFF_CYCLE_COPY.markFailed}
      onSubmit={() =>
        markFailed.mutate(
          { noticeId: notice.id, companyId: notice.company_id, reason: reason.trim() },
          {
            onSuccess: () => {
              notify('warning', OFF_CYCLE_COPY.failed);
              onDone();
            },
          },
        )
      }
    >
      <FormError error={markFailed.error} />
      <TextField
        label={OFF_CYCLE_COPY.failureReasonLabel}
        hint={OFF_CYCLE_COPY.failureReasonHint}
        required
        value={reason}
        onChange={setReason}
      />
      <div className="form__actions">
        <button type="submit" className="button button--danger" disabled={markFailed.isPending}>
          {OFF_CYCLE_COPY.confirmFailed}
        </button>
        <button type="button" className="button button--ghost" onClick={onDone}>
          {OFF_CYCLE_COPY.cancel}
        </button>
      </div>
    </Form>
  );
}
